import { useEffect, useState } from "react";
import JacksonHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";

const navItems = [
  { label: "Processing", href: "#processing" },
  { label: "Services", href: "#services" },
  { label: "Local Area", href: "#local-area" },
  { label: "Timing", href: "#timing" },
  { label: "Destinations", href: "#destinations" },
  { label: "Facilities", href: "#facilities" },
  { label: "Compare", href: "#compare" },
  { label: "FAQ", href: "#faq" },
  { label: "Resources", href: "#resources" },
];

const faqs = [
  { q: "How fast can I get a passport in Jackson, MS?", a: "Our emergency service delivers passports in as little as 24 hours. Standard expedited takes 3–5 business days. Standard takes 7–10 business days. For state officials and UMMC medical professionals with urgent travel needs, we handle the most time-sensitive requests." },
  { q: "Where is the nearest passport acceptance facility in Jackson?", a: "The main facility is the Jackson Main Post Office on West Capitol Street. The Hinds County Chancery Clerk's Office also accepts applications. We direct you to whichever has the shortest wait time." },
  { q: "Can Mississippi state government employees get expedited passports?", a: "Absolutely. We regularly assist state agency officials, legislators, and their staff with urgent domestic and international travel needs. Our emergency service handles short-notice requests for official government travel." },
  { q: "Do I need an appointment for a passport in Jackson?", a: "Most Jackson-area facilities are walk-in only. We advise on arrival times and which location is currently least busy." },
  { q: "What documents do I need for a passport in Jackson?", a: "You need proof of U.S. citizenship (certified birth certificate with raised seal), a valid government-issued photo ID, a compliant passport photo, and the DS-11 or DS-82 form. We provide a full checklist and verify all documents before you visit." },
  { q: "Is the New Orleans Passport Agency closer than Memphis for Jackson residents?", a: "The New Orleans Regional Passport Agency is about 3 hours south. The Memphis agency is 3 hours north. For emergency processing, our courier service is faster and more convenient than either option." },
  { q: "Can UMMC doctors and nurses get expedited passports for international medical travel?", a: "Yes. We regularly assist medical professionals from the University of Mississippi Medical Center with international conference travel and humanitarian mission requests." },
  { q: "How much does passport expediting cost from Jackson?", a: "Government fees are $130–$190. Our service fees start at $99. We guarantee the best price — find a lower rate and we'll match it." },
];

const destinations = [
  { name: "Cancun", country: "Mexico", flag: "🇲🇽", desc: "Most popular spring break and family vacation from Jackson. Connections via Dallas or Atlanta." },
  { name: "New Orleans Cruises", country: "Various", flag: "🚢", desc: "New Orleans cruise port is 3 hours away — a popular embarkation point for Jackson families." },
  { name: "London", country: "UK", flag: "🇬🇧", desc: "Business and government travel hub. State trade missions and professional conferences." },
  { name: "Jamaica", country: "Jamaica", flag: "🇯🇲", desc: "Church mission trips and spring break from the Jackson metropolitan area." },
  { name: "Europe", country: "Various", flag: "🇪🇺", desc: "JSU and Millsaps study abroad programs and summer cultural tours." },
  { name: "Caribbean", country: "Various", flag: "🏝️", desc: "Caribbean cruise packages popular with Jackson suburban families." },
];

const facilities = [
  { name: "Jackson Main Post Office", address: "401 E South St, Jackson, MS 39201", phone: "(601) 351-7910", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "20–50 min" },
  { name: "Hinds County Chancery Clerk", address: "316 S President St, Jackson, MS 39201", phone: "(601) 968-6501", hours: "Mon–Fri 8:00 AM – 4:30 PM", wait: "15–35 min" },
  { name: "Madison Post Office", address: "135 Post Office Dr, Madison, MS 39110", phone: "(601) 856-5631", hours: "Mon–Fri 9:00 AM – 4:00 PM", wait: "10–20 min" },
];

const testimonials = [
  { name: "Representative David P.", city: "Fondren, Jackson", stars: 5, text: "Had a last-minute trade mission to London. Fast Passport Center turned my renewal around in 48 hours. Critical service for Mississippi officials." },
  { name: "Dr. Kimberly T.", city: "Ridgeland", stars: 5, text: "UMMC physician attending a medical conference in Tokyo on short notice. Passport renewed in 4 days. Incredibly professional." },
  { name: "The Johnson Family", city: "Flowood", stars: 5, text: "Four passports for our Caribbean cruise. Done in 5 days. Would have cancelled the trip without this service." },
];

export default function JacksonPage() {
  useEffect(() => {
    document.title = "Emergency Passport Jackson, MS — Expedited Same-Day Service | Fast Passport Center";
    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    const setLink = (rel: string, href: string) => {
      let el = document.querySelector(`link[rel="${rel}"]`);
      if (!el) { el = document.createElement("link"); el.setAttribute("rel", rel); document.head.appendChild(el); }
      el.setAttribute("href", href);
    };
    setLink("canonical", "https://fastpassportcenter.com/jackson/");
    setMeta("description", "Emergency and expedited passport services in Jackson, MS — as fast as 24 hours. Serving Mississippi state officials, UMMC medical professionals, and central Mississippi families. A+ BBB rated.");
    setMeta("geo.placename", "Jackson, Mississippi");
    setMeta("geo.region", "US-MS");
    setMeta("geo.position", "32.2988;-90.1848");
    setMeta("ICBM", "32.2988, -90.1848");
    return () => { document.querySelector('link[rel="canonical"]')?.remove(); };
  }, []);

  return (
    <div className="font-sans">
      <JacksonHeroSection />
      <StickyNav />
      <ProcessingSection />
      <ServicesSection />
      <LocalAreaSection />
      <TravelTimingSection />
      <DestinationsSection />
      <TestimonialsSection />
      <AcceptanceFacilitiesSection />
      <ComparisonSection />
      <MistakesSection />
      <HowItWorksSection />
      <FAQSection />
      <HelpfulResourcesSection />
      <SEOCrossLinks currentRoute="/cities/jackson" state="MS" />
      <FinalCTA />
    </div>
  );
}

function StickyNav() {
  const [active, setActive] = useState("");
  useEffect(() => {
    const handle = () => {
      const sections = navItems.map((n) => document.querySelector(n.href));
      const scroll = window.scrollY + 120;
      for (let i = sections.length - 1; i >= 0; i--) {
        const s = sections[i];
        if (s && s.getBoundingClientRect().top + window.scrollY <= scroll) { setActive(navItems[i].href); return; }
      }
      setActive("");
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);
  return (
    <div className="sticky top-[70px] z-40 bg-white border-b border-gray-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center gap-1 overflow-x-auto py-2">
          {navItems.map((item) => (<a key={item.href} href={item.href} className={`whitespace-nowrap px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${active === item.href ? "bg-[#1A365D] text-white" : "text-gray-600 hover:bg-gray-100"}`}>{item.label}</a>))}
        </div>
      </div>
    </div>
  );
}

function ProcessingSection() {
  return (
    <section id="processing" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Processing Options</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How Fast Can You Get a Passport in Jackson?</h2>
          <p className="text-gray-500 max-w-2xl mx-auto">Government official, medical professional, or family traveler — three tiers to match your timeline.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Emergency", time: "24–48 Hours", price: "From $299", badge: "Fastest", desc: "For state officials and UMMC professionals with immediate international travel needs.", features: ["24-hour turnaround available", "Hand-carry courier service", "Real-time tracking", "Phone support 7 days/week"] },
            { title: "Expedited", time: "3–5 Business Days", price: "From $149", badge: "Most Popular", desc: "The most popular option for Jackson metro families and professionals.", features: ["3–5 day guaranteed delivery", "Application review included", "Photo compliance check", "Email + phone support"] },
            { title: "Standard", time: "7–10 Business Days", price: "From $99", badge: "Best Value", desc: "Still far faster than government standard. Ideal for planned travel.", features: ["7–10 day processing", "Complete application guidance", "Document checklist", "Email support"] },
          ].map((plan, i) => (
            <div key={i} className={`bg-white border rounded-xl p-7 flex flex-col ${plan.badge === "Most Popular" ? "border-[#F6C344] ring-1 ring-[#F6C344]" : "border-gray-200"}`}>
              <div className="flex items-center justify-between mb-4">
                <span className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${plan.badge === "Fastest" ? "bg-red-100 text-red-700" : plan.badge === "Most Popular" ? "bg-[#F6C344]/20 text-[#1A365D]" : "bg-gray-100 text-gray-700"}`}>{plan.badge}</span>
                <span className="text-[#1A365D] font-bold text-lg">{plan.price}</span>
              </div>
              <h3 className="font-bold text-[#1A365D] text-xl mb-1">{plan.title}</h3>
              <p className="text-[#F6C344] font-semibold text-sm mb-3">{plan.time}</p>
              <p className="text-gray-500 text-sm leading-relaxed mb-5 flex-1">{plan.desc}</p>
              <ul className="space-y-2 mb-6">{plan.features.map((f, j) => <li key={j} className="flex items-center gap-2 text-sm text-gray-600"><i className="ri-check-line text-[#F6C344] flex-shrink-0"></i>{f}</li>)}</ul>
              <a href="https://fastpassportcenter.com/checkout/" className="block text-center bg-[#1A365D] hover:bg-[#0f2341] text-white font-semibold text-sm py-3 rounded-lg transition-all whitespace-nowrap">Get Started</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ServicesSection() {
  const services = [
    { icon: "ri-passport-line", title: "New Passport", desc: "First-time applications for Jackson and central Mississippi residents." },
    { icon: "ri-refresh-line", title: "Passport Renewal", desc: "Fast renewal for state officials and business professionals." },
    { icon: "ri-alert-line", title: "Lost/Stolen", desc: "Emergency replacement with DS-64 form guidance." },
    { icon: "ri-user-smile-line", title: "Child Passport", desc: "Minors under 16 with complete parental consent guidance." },
    { icon: "ri-time-line", title: "Emergency", desc: "24-hour processing for urgent government and medical travel." },
    { icon: "ri-edit-line", title: "Name Change", desc: "Post-marriage or legal name updates." },
  ];
  return (
    <section id="services" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Our Services</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Jackson, MS Travelers</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4"><i className={`${s.icon} text-[#1A365D] text-xl`}></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LocalAreaSection() {
  return (
    <section id="local-area" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-10">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Area</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services in Jackson, Mississippi</h2>
        </div>
        <div className="max-w-3xl mx-auto text-gray-600 text-base leading-relaxed space-y-5">
          <p>Jackson, Mississippi's capital and largest city, sits at the intersection of government, healthcare, education, and emerging technology. With a metro population of over 600,000 spanning Hinds, Rankin, and Madison counties, Jackson serves as the administrative and economic engine of the entire state — and its residents have some of the most diverse international travel needs of any city in the South.</p>
          <p>Mississippi state government generates significant passport demand. Legislators, agency directors, and their staff frequently travel internationally for trade missions to Europe and Asia, diplomatic exchanges, and intergovernmental conferences. The Governor's Office and Department of Commerce both maintain active international business development programs. Sudden travel requirements — sometimes with as little as 48 hours notice — are common at the Capitol complex.</p>
          <p>The University of Mississippi Medical Center (UMMC), the state's only academic medical center, employs thousands of physicians, nurses, and researchers. International medical conferences, humanitarian missions, and research exchange programs regularly require expedited passport processing. The Jackson metro's other medical institutions — Baptist Health, St. Dominic's, and Merit Health — collectively employ thousands of healthcare workers with international travel needs.</p>
          <p>Jackson State University, Millsaps College, and Belhaven University each have growing international programs and study abroad offerings. Suburban communities like Ridgeland, Madison, Flowood, and Pearl are home to families who increasingly travel internationally for vacations and cruises. The Jackson-Medgar Wiley Evers International Airport (JAN) offers connections through Atlanta, Dallas, and Charlotte to international destinations. With three hours to the New Orleans cruise port and three hours to Memphis, Jackson is also a popular home base for cruise travelers who need passports on schedule.</p>
        </div>
      </div>
    </section>
  );
}

function TravelTimingSection() {
  return (
    <section id="timing" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Travel Timing</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">When Jackson Travelers Need Passports</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            { month: "Year-Round", title: "Government Travel", desc: "State officials and agency staff travel internationally on unpredictable government timelines." },
            { month: "March–April", title: "Spring Break", desc: "Cancun and Caribbean destinations peak. New Orleans cruise embarkations surge." },
            { month: "May–June", title: "Summer Travel", desc: "Families plan European and island vacations. Study abroad departures begin." },
            { month: "July–August", title: "Medical Missions", desc: "Church and hospital medical mission teams depart for Caribbean, Africa, and Latin America." },
            { month: "September–October", title: "Renewal Season", desc: "Ideal time to renew before the holiday travel rush." },
            { month: "November–December", title: "Holiday Rush", desc: "Winter cruise bookings and holiday international family travel surge." },
          ].map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-wider mb-2">{t.month}</p>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{t.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function DestinationsSection() {
  return (
    <section id="destinations" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Where Jackson Travelers Go</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {destinations.map((d, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all">
              <div className="flex items-center gap-3 mb-3">
                <span className="text-2xl">{d.flag}</span>
                <div><h3 className="font-bold text-[#1A365D] text-lg">{d.name}</h3><p className="text-gray-400 text-xs">{d.country}</p></div>
              </div>
              <p className="text-gray-500 text-sm leading-relaxed">{d.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function TestimonialsSection() {
  return (
    <section id="testimonials" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Reviews</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">What Jackson Travelers Say</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center gap-1 mb-3">{[...Array(t.stars)].map((_, j) => <i key={j} className="ri-star-fill text-[#F6C344] text-sm"></i>)}</div>
              <p className="text-gray-600 text-sm leading-relaxed mb-4 italic">"{t.text}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#EEF2F8] flex items-center justify-center"><i className="ri-user-line text-[#1A365D]"></i></div>
                <div><p className="font-semibold text-[#1A365D] text-sm">{t.name}</p><p className="text-gray-400 text-xs">{t.city}</p></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function AcceptanceFacilitiesSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="facilities" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Acceptance Facilities</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Acceptance Facilities Near Jackson</h2>
        </div>
        <div className="space-y-4 max-w-3xl mx-auto">
          {facilities.map((f, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8]"><i className="ri-map-pin-line text-[#1A365D]"></i></div>
                  <div><h3 className="font-bold text-[#1A365D] text-base">{f.name}</h3><p className="text-gray-400 text-xs">{f.address}</p></div>
                </div>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl`}></i>
              </button>
              {open === i && (
                <div className="px-5 pb-5 pt-0 border-t border-gray-100">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Phone</p><p className="text-[#1A365D] text-sm font-medium">{f.phone}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Hours</p><p className="text-[#1A365D] text-sm font-medium">{f.hours}</p></div>
                    <div className="bg-[#F7F8FA] rounded-lg p-3"><p className="text-gray-400 text-xs mb-1">Avg Wait</p><p className="text-[#1A365D] text-sm font-medium">{f.wait}</p></div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ComparisonSection() {
  const rows = [
    { feature: "Processing Speed", us: "As fast as 24 hours", them: "10–13 weeks standard" },
    { feature: "Office Visit Required", us: "No — mail or drop-off", them: "Yes — in-person required" },
    { feature: "Application Review", us: "Expert review before submission", them: "No review — errors cause delays" },
    { feature: "Tracking", us: "Real-time status updates", them: "Limited status info" },
    { feature: "Customer Support", us: "Phone + email, 7 days/week", them: "Phone only, limited hours" },
    { feature: "Price Guarantee", us: "Best price guarantee", them: "No price matching" },
    { feature: "Government Travel Support", us: "Full support for official travel", them: "No priority for government travel" },
    { feature: "Refund Policy", us: "Satisfaction guarantee", them: "No refunds after submission" },
  ];
  return (
    <section id="compare" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Compare</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. DIY Government Processing</h2>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="grid grid-cols-3 border-b border-gray-200 bg-[#EEF2F8]">
            <div className="p-4 font-bold text-[#1A365D] text-sm">Feature</div>
            <div className="p-4 font-bold text-[#1A365D] text-sm text-center">Fast Passport Center</div>
            <div className="p-4 font-bold text-gray-500 text-sm text-center">Government Standard</div>
          </div>
          {rows.map((row, i) => (
            <div key={i} className={`grid grid-cols-3 ${i !== rows.length - 1 ? "border-b border-gray-100" : ""}`}>
              <div className="p-4 text-sm text-gray-600 font-medium">{row.feature}</div>
              <div className="p-4 text-sm text-[#1A365D] font-semibold text-center bg-[#F7F8FA]/50">{row.us}</div>
              <div className="p-4 text-sm text-gray-500 text-center">{row.them}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function MistakesSection() {
  const mistakes = [
    { title: "Waiting on Government Timelines", desc: "Jackson state officials often assume government status speeds up the process. It doesn't — our courier service is the only way to guarantee a fast turnaround." },
    { title: "Wrong Photo", desc: "Jackson passport photos from convenience stores are frequently rejected. We verify your photo before submission." },
    { title: "Missing Birth Certificate Seal", desc: "A birth certificate without a raised or embossed state seal is rejected. We verify all documents before your facility visit." },
    { title: "Late Application Start", desc: "Many Jackson residents start the passport process just 3 weeks before travel. We recommend 6–8 weeks for standard processing." },
    { title: "Wrong Application Form", desc: "DS-11 vs DS-82 confusion causes significant delays. We identify the correct form for your specific situation." },
  ];
  return (
    <section id="mistakes" className="py-16 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Common Mistakes</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Mistakes Jackson Applicants Make</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {mistakes.map((m, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-4"><i className="ri-close-circle-line text-red-500 text-xl"></i></div>
              <h3 className="font-bold text-[#1A365D] text-lg mb-2">{m.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowItWorksSection() {
  const steps = [
    { num: "01", title: "Choose Your Service", desc: "Emergency, expedited, or standard — based on your departure date." },
    { num: "02", title: "Complete Application", desc: "We guide you through DS-11 or DS-82 step by step." },
    { num: "03", title: "Visit Acceptance Facility", desc: "We direct you to the Jackson facility with the shortest wait." },
    { num: "04", title: "We Handle Processing", desc: "Hand-carried to the State Department for priority processing." },
    { num: "05", title: "Receive Your Passport", desc: "Secure delivery with real-time tracking throughout." },
  ];
  return (
    <section id="how-it-works" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">How It Works</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">5 Steps to Your Passport</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="text-center">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-white font-bold text-lg mx-auto mb-4">{s.num}</div>
              <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function FAQSection() {
  const [open, setOpen] = useState<number | null>(0);
  return (
    <section id="faq" className="py-16 md:py-24 bg-white">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">FAQ</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Jackson, MS Passport Questions</h2>
        </div>
        <div className="space-y-3">
          {faqs.map((faq, i) => (
            <div key={i} className="border border-gray-200 rounded-lg bg-white overflow-hidden">
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-5 text-left">
                <span className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</span>
                <i className={`${open === i ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-gray-400 text-xl flex-shrink-0`}></i>
              </button>
              {open === i && <div className="px-5 pb-5 pt-0 border-t border-gray-100"><p className="text-gray-500 text-sm leading-relaxed">{faq.a}</p></div>}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HelpfulResourcesSection() {
  return (
    <section id="resources" className="py-16 md:py-24 bg-[#F7F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="text-center mb-12">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Resources</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Jackson Travelers</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { title: "Travel.State.Gov", desc: "Official U.S. Department of State passport information.", link: "https://travel.state.gov/content/travel/en/passports.html" },
            { title: "Jackson Airport (JAN)", desc: "Jackson-Medgar Wiley Evers International Airport connections.", link: "https://www.jmaa.com/" },
            { title: "CDC Travel Health", desc: "Health recommendations and vaccination requirements for international travel.", link: "https://wwwnc.cdc.gov/travel" },
          ].map((r, i) => (
            <a key={i} href={r.link} target="_blank" rel="noopener noreferrer" className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#F6C344] transition-all group">
              <h3 className="font-bold text-[#1A365D] text-lg mb-2 group-hover:text-[#F6C344] transition-colors">{r.title}</h3>
              <p className="text-gray-500 text-sm leading-relaxed mb-3">{r.desc}</p>
              <span className="inline-flex items-center gap-1 text-[#1A365D] text-sm font-semibold group-hover:text-[#F6C344] transition-colors">Visit <i className="ri-arrow-right-line"></i></span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}

function FinalCTA() {
  return (
    <section className="py-20 md:py-28 bg-[#1A365D]">
      <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
        <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-5">Ready to Get Your Passport in Jackson?</h2>
        <p className="text-white/70 text-lg max-w-2xl mx-auto mb-8">State official, medical professional, or Mississippi family — we'll get your passport ready on your timeline.</p>
        <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all hover:scale-105 whitespace-nowrap">Start My Passport <i className="ri-arrow-right-line"></i></a>
        <p className="text-white/50 text-sm mt-6">Call us at <a href="tel:8772530084" className="text-[#F6C344] hover:underline">877-253-0084</a> for immediate assistance</p>
      </div>
    </section>
  );
}